
import React, { useState, useEffect, useRef } from "react";
import { 
  FiTrash2, 
  FiPlus,
  FiSearch,
  FiX,
  FiSave,
  FiRefreshCcw
} from "react-icons/fi";
import { toast } from "react-toastify";

const Note = ({ darkMode }) => {
  const [notes, setNotes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [sortColumn, setSortColumn] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  const searchInputRef = useRef(null);
  const textareaRef = useRef(null);
  const rowsPerPage = 12;

  // Apply dark mode class to body
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Load notes from localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem("rich_notes");
    if (savedNotes) {
      try {
        const parsedNotes = JSON.parse(savedNotes);
        setNotes(parsedNotes);
      } catch (e) {
        console.error("Error loading notes:", e);
      }
    } else {
      const sampleNotes = [
        { 
          id: Date.now(), 
          content: "Welcome to Notes! You can write anything here.", 
          createdAt: new Date().toISOString(), 
          updatedAt: new Date().toISOString() 
        },
        { 
          id: Date.now() + 1, 
          content: "Milk, Eggs, Bread", 
          createdAt: new Date().toISOString(), 
          updatedAt: new Date().toISOString() 
        },
        { 
          id: Date.now() + 2, 
          content: "Meeting Notes: Discuss Q4 goals with team", 
          createdAt: new Date().toISOString(), 
          updatedAt: new Date().toISOString() 
        }
      ];
      setNotes(sampleNotes);
      localStorage.setItem("rich_notes", JSON.stringify(sampleNotes));
    }
  }, []);

  // Save notes
  useEffect(() => {
    if (notes.length > 0) {
      localStorage.setItem("rich_notes", JSON.stringify(notes));
    }
  }, [notes]);

  const createNewNote = () => {
    const newNote = {
      id: Date.now(),
      content: "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setNotes([newNote, ...notes]);
    setSelectedNote(newNote);
    setIsEditing(true);
    setEditContent("");
    toast.success("New note created!");
  };

  const saveNote = () => {
    if (!selectedNote) return;
    
    let finalContent = editContent.trim();
    if (!finalContent) {
      finalContent = "Empty note...";
    }
    
    const updatedNote = {
      ...selectedNote,
      content: finalContent,
      updatedAt: new Date().toISOString()
    };
    setNotes(prev => prev.map(n => n.id === selectedNote.id ? updatedNote : n));
    setSelectedNote(updatedNote);
    setIsEditing(false);
    toast.success("Note saved!");
  };

  const deleteNote = (noteId, e) => {
    e?.stopPropagation();
    if (window.confirm("Delete this note?")) {
      const newNotes = notes.filter(n => n.id !== noteId);
      setNotes(newNotes);
      if (selectedNote?.id === noteId) {
        setSelectedNote(null);
        setIsEditing(false);
      }
      toast.success("Note deleted!");
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FiRefreshCcw size={9} className="opacity-50" />;
    return sortOrder === "asc" ? "↑" : "↓";
  };

  // Filter notes based on search
  let filteredNotes = notes.filter(note => {
    const plainText = note.content || "";
    return plainText.toLowerCase().includes(searchText.toLowerCase());
  });

  filteredNotes.sort((a, b) => {
    let aVal = a[sortColumn] || "";
    let bVal = b[sortColumn] || "";
    if (sortColumn === "createdAt" || sortColumn === "updatedAt") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (sortOrder === "asc") return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  // Pagination
  const totalPages = Math.ceil(filteredNotes.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentNotes = filteredNotes.slice(startIdx, startIdx + rowsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  const handleSearchExpand = () => {
    setIsSearchExpanded(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const clearSearch = () => {
    setSearchText("");
    setIsSearchExpanded(false);
    setCurrentPage(1);
  };

  const openNoteForEdit = (note) => {
    setSelectedNote(note);
    setEditContent(note.content);
    setIsEditing(true);
  };

  // Focus textarea when modal opens
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  const getPreviewText = (content) => {
    if (!content) return "Empty note...";
    if (content.length <= 80) return content;
    return content.substring(0, 80) + "...";
  };

  const containerClass = `h-full flex flex-col transition-all duration-300 bg-[var(--bg-primary)]`;
  const headerClass = `p-2 border-b transition-colors duration-200 bg-[var(--bg-header)] border-[var(--border-color)]`;
  const searchBoxClass = `relative flex items-center rounded-md border transition-all duration-300
    bg-[var(--input-bg)] border-[var(--input-border)]
    ${isSearchExpanded ? 'w-56' : 'w-auto'}`;
  const searchInputClass = `w-full outline-none text-xs py-1.5 px-1 bg-transparent text-[var(--input-text)] placeholder:text-[var(--text-muted)]`;
  const tableHeaderClass = `sticky top-0 z-10 text-[11px] font-semibold bg-[var(--bg-header)] text-[var(--text-primary)]`;
  const tableCellClass = `px-2 py-1.5 border text-xs border-[var(--border-color)] text-[var(--text-secondary)]`;
  const noteRowClass = `cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-card)]`;

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={headerClass}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm">📝</span>
            <h2 className={`text-xs font-semibold text-[var(--text-primary)]`}>Notes</h2>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg-card)] text-[var(--text-muted)]`}>
              {notes.length}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <div className={searchBoxClass}>
              <button onClick={handleSearchExpand} className={`p-1.5 rounded-l-md transition-all ${isSearchExpanded ? 'bg-blue-500 text-white' : 'hover:bg-black/10 text-[var(--text-muted)]'}`}>
                <FiSearch size={11} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${isSearchExpanded ? 'w-36' : 'w-0'}`}>
                <input 
                  ref={searchInputRef} 
                  type="text" 
                  placeholder="Search notes..." 
                  className={searchInputClass} 
                  value={searchText} 
                  onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }} 
                />
              </div>
              {isSearchExpanded && searchText && (
                <button onClick={clearSearch} className="p-1.5 rounded-r-md hover:bg-black/10 text-[var(--text-muted)]">
                  <FiX size={11} />
                </button>
              )}
            </div>

            <button 
              onClick={createNewNote} 
              className="p-1.5 rounded-md hover:bg-black/10 text-[var(--text-muted)]" 
              title="New Note"
            >
              <FiPlus size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Notes Table */}
      <div className="flex-1 overflow-auto p-2">
        {filteredNotes.length > 0 && (
          <>
            <h3 className={`text-[10px] font-semibold mb-2 text-[var(--text-muted)]`}>📄 All Notes</h3>
            <table className="w-full text-[11px]">
              <thead className={tableHeaderClass}>
                <tr>
                  <th className="px-2 py-1 text-left">Note</th>
                  <th className="w-20 px-2 py-1 text-left cursor-pointer" onClick={() => handleSort("updatedAt")}>
                    Updated {getSortIcon("updatedAt")}
                  </th>
                  <th className="w-12 px-2 py-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentNotes.map(note => (
                  <tr key={note.id} className={noteRowClass} onClick={() => openNoteForEdit(note)}>
                    <td className={tableCellClass}>
                      <div className="text-[11px] break-words">
                        {getPreviewText(note.content)}
                      </div>
                    </td>
                    <td className={`${tableCellClass} text-left text-[10px]`}>
                      {formatDate(note.updatedAt)}
                    </td>
                    <td className={`${tableCellClass} text-center`}>
                      <div className="flex items-center justify-center gap-1">
                        <button 
                          onClick={(e) => deleteNote(note.id, e)} 
                          className="p-0.5 rounded hover:bg-red-500/20" 
                          title="Delete"
                        >
                          <FiTrash2 size={10} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className={`flex justify-center items-center gap-1 mt-3 pt-2 border-t border-[var(--border-color)]`}>
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                  disabled={currentPage === 1} 
                  className={`px-1.5 py-0.5 text-[10px] rounded ${currentPage === 1 ? 'text-[var(--text-light)] cursor-not-allowed' : 'hover:bg-black/10 text-[var(--text-muted)]'}`}
                >
                  ←
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i + 1;
                  if (totalPages > 5 && currentPage > 3) pageNum = currentPage - 2 + i;
                  if (pageNum <= totalPages && pageNum > 0) return (
                    <button 
                      key={pageNum} 
                      onClick={() => setCurrentPage(pageNum)} 
                      className={`w-5 h-4 text-[10px] rounded ${currentPage === pageNum ? 'bg-blue-500 text-white' : 'hover:bg-black/10 text-[var(--text-muted)]'}`}
                    >
                      {pageNum}
                    </button>
                  );
                  return null;
                })}
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                  disabled={currentPage === totalPages} 
                  className={`px-1.5 py-0.5 text-[10px] rounded ${currentPage === totalPages ? 'text-[var(--text-light)] cursor-not-allowed' : 'hover:bg-black/10 text-[var(--text-muted)]'}`}
                >
                  →
                </button>
              </div>
            )}
          </>
        )}

        {filteredNotes.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className={`text-3xl mb-2 text-[var(--text-muted)]`}>📝</div>
            <p className={`text-[11px] mb-2 text-[var(--text-muted)]`}>No notes found</p>
            <button 
              onClick={createNewNote} 
              className={`px-2 py-0.5 rounded text-[10px] bg-blue-500 hover:bg-blue-600 text-white`}
            >
              + New Note
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal - Simple Textarea with Dark Mode Support */}
      {isEditing && selectedNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className={`w-[600px] max-w-[90vw] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-[#1f2937]' : 'bg-white'}`}>
            {/* Simple Header */}
            <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700 bg-[#1f2937]' : 'border-gray-200 bg-white'}`}>
              <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Edit Note
              </h3>
              <div className="flex items-center gap-1">
                <button 
                  onClick={saveNote} 
                  className={`p-1.5 rounded transition-colors ${darkMode ? 'hover:bg-gray-700 text-green-400' : 'hover:bg-gray-100 text-green-600'}`}
                  title="Save"
                >
                  <FiSave size={16} />
                </button>
                <button 
                  onClick={() => deleteNote(selectedNote.id)} 
                  className={`p-1.5 rounded transition-colors ${darkMode ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-100 text-red-600'}`}
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
                <button 
                  onClick={() => setIsEditing(false)} 
                  className={`p-1.5 rounded transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                  title="Close"
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>

            {/* Simple Textarea */}
            <textarea
              ref={textareaRef}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className={`w-full p-4 text-sm outline-none resize-none ${
                darkMode ? 'bg-[#1f2937] text-gray-200' : 'bg-white text-gray-700'
              }`}
              style={{ minHeight: '300px' }}
              placeholder="Write your note here..."
            />
            
            <div className={`p-2 text-[10px] text-center border-t ${darkMode ? 'border-gray-700 text-gray-400 bg-[#1f2937]' : 'border-gray-200 text-gray-500 bg-white'}`}>
              Last edited: {formatDate(selectedNote.updatedAt)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Note;